import React, { useContext, useEffect, useRef, useState } from "react";
import "./TeaserModeration.scss";
import ModerationCountContext from "../../context/ModerationCountContext";
import withQs from "../../utils/WithQs";
import {
  TeaserDetailService,
  TeaserForModerationService,
  TeaserModerateService
} from "../../utils/api";
import useCampaignInfoEffect from "../../effects/useCampaignInfoEffect";

import GlobalsContext from "../../context/GlobalsContext";
import WsContext from "../../context/WsContext";
import NotificationContext from "../../context/NotificationContext";

import { teaserStatus } from "../../constants";

import * as perm from "../../utils/perm";

import usePermissions from "../../hooks/usePermissions";

import TeaserModerationView from "./TeaserModerationView";

import useCategoryRejectReasons from "../../hooks/useCategoryRejectReasons";
import { useTeaserFormSpellChecker } from "../../hooks/useTeaserFormSpellChecker";

function getAvailableGroups(permissions) {
  const groups = [];

  if (perm.canModerateExchange(permissions)) {
    groups.push("exchange");
  }

  if (perm.canModerateMedia(permissions)) {
    groups.push("media");
  }

  if (perm.canModerateGoods(permissions)) {
    groups.push("goods");
  }

  return groups;
}

function getAvailableOptions(groups, counts) {
  const group2name = {
    exchange: "Обмен",
    media: "Медиа",
    goods: "Товары"
  };

  return groups.map(group => ({
    title: group2name[group],
    count: counts[group],
    code: group
  }));
}

function getGroup(count, search, availableGroups) {
  if (search.group && count[search.group] != 0) {
    return search.group;
  }

  for (const group of availableGroups) {
    if (count[group] !== 0) {
      return group;
    }
  }
}

function useIsFirstLoad(initial, loading) {
  const [isFirstLoad, setIsFirstLoad] = useState(initial);

  useEffect(() => {
    if (isFirstLoad && !loading) {
      setIsFirstLoad(false);
    }
  }, [loading]);

  return isFirstLoad;
}

function isValid(fields) {
  return (
    !!fields.cid &&
    !!fields.category &&
    !!fields.title &&
    !!fields.image &&
    !!fields.url &&
    fields.type !== undefined &&
    fields.type !== null &&
    fields.type !== "" &&
    fields.published == teaserStatus.moderate
  );
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function useCurrent(value) {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  });

  return ref;
}

const TeaserModeration = ({ search, replaceSearch }) => {
  const permissions = usePermissions();
  const countContext = useContext(ModerationCountContext);
  const globalsContext = useContext(GlobalsContext);
  const wsContext = useContext(WsContext);
  const notificationContext = useContext(NotificationContext);

  const isFirstLoad = useIsFirstLoad(true, countContext.loading);
  const [state, setState] = useState({
    teaser: null,
    exclude: [],
    group: null,
    empty: null,
    errors: {},
    showRefreshButton: false,
    loading: true,
    showReloadButton: false
  });

  const rejectReasons = useCategoryRejectReasons((state.teaser || {}).category);

  const prevCount = usePrevious(countContext.count);

  const currentTeaser = useCurrent(state.teaser);
  const currentLoading = useCurrent(state.loading);

  const spellChecker = useTeaserFormSpellChecker();

  const {
    info: campaignInfo,
    loading: campaignInfoLoading
  } = useCampaignInfoEffect((state.teaser || {}).cid);

  const availableGroups = getAvailableGroups(permissions);
  const availableOptions = getAvailableOptions(
    availableGroups,
    countContext.count
  );

  const loadNextTeaser = (group, exclude) => {
    setState(prev => ({ ...prev, errors: {}, loading: true }));

    TeaserForModerationService({ group, exclude })
      .then(data => {
        if (data.teaser) {
          setState(prev => ({
            ...prev,
            teaser: data.teaser,
            loading: false
          }));
          return;
        }

        if (data.count.total == 0) {
          setState(prev => ({
            ...prev,
            empty: true,
            group: null,
            teaser: null,
            loading: false
          }));
        } else {
          const group =
            getGroup(
              { ...data.count, [state.group]: 0 },
              {},
              availableGroups
            ) || state.group;
          setState(prev => ({ ...prev, group, exclude: [], loading: true }));
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  // effects

  useEffect(() => {
    wsContext.eventEmitter.subscribe("teasers_update", data => {
      if (currentLoading.current) {
        return;
      }

      if (
        currentTeaser.current &&
        data.ids.includes(currentTeaser.current.id)
      ) {
        setShowRefresh(true);
      }
    });
  }, []);

  useEffect(() => {
    if (!isFirstLoad) {
      if (prevCount.total == 0 && countContext.count.total != 0) {
        setState(prev => ({ ...prev, showRefreshButton: true }));
      }
    }
  });

  useEffect(() => {
    if (!isFirstLoad) {
      const group =
        getGroup(countContext.count, search, availableGroups) || null;
      const empty = countContext.count.total == 0;
      const loading = !empty;
      setState(prev => ({ ...prev, group, empty, loading }));
    }
  }, [isFirstLoad]);

  useEffect(() => {
    if (state.group) {
      loadNextTeaser(state.group, state.exclude);
    }
  }, [state.group, state.exclude]);

  useEffect(() => {
    if (state.group) {
      replaceSearch({ group: state.group });
    }
  }, [state.group]);

  // callbacks

  const onGroupChange = group => {
    setState(prev => ({ ...prev, group, exclude: [] }));
  };

  const onSkipClick = () => {
    if (countContext.count.total == 1) {
      return;
    }
    setState(prev => ({
      ...prev,
      exclude: [...prev.exclude, state.teaser.id]
    }));
  };

  const onCampaignChange = item => {
    setState(prev => ({
      ...prev,
      teaser: { ...prev.teaser, cid: item.value }
    }));
  };

  const onReasonChange = ({ selectedItems: selected }) => {
    setState(prev => ({
      ...prev,
      teaser: { ...prev.teaser, reject_reasons: selected }
    }));
  };

  const onFormChange = payload => {
    setState(prev => ({ ...prev, teaser: { ...prev.teaser, ...payload } }));
  };

  // helpers

  const setLoading = loading => {
    setState(prev => ({ ...prev, loading }));
  };

  const setShowRefresh = showReloadButton => {
    setState(prev => ({ ...prev, showReloadButton: showReloadButton }));
  };

  const onModerationClick = () => {
    const submit = () => {
      setLoading(true);

      TeaserModerateService({ tid: state.teaser.id, data: state.teaser })
        .then(() => {
          loadNextTeaser(state.group, state.exclude);
        })
        .catch(e => {
          if (e.code == "TEASER_ALREADY_MODERATED") {
            notificationContext.showSimpleNotification({
              title: "Анонс уже был проверен",
              text: "Сохранение не совершено, анонс уже был проверен ранее.",
              color: "red"
            });
            loadNextTeaser(state.group, state.exclude);
          } else if (e.code == "TEASER_ALREADY_UPDATED") {
            setState(prev => ({ ...prev, showReloadButton: true }));
          } else if (e.errors) {
            setState(prev => ({ ...prev, errors: e.errors }));

            if (e.errors.update_date) {
              notificationContext.showSimpleNotification({
                title: "Анонс был изменен",
                text: "Сохранение не совершено, анонс был изменен.",
                color: "red"
              });
            }
          }
        })
        .finally(() => {
          setLoading(false);
        });
    };

    spellChecker.onSubmit({ submit, fields: state.teaser });
  };

  const onRefreshClick = () => {
    const group = getGroup(countContext.count, {}, availableGroups);
    setState(prev => ({
      ...prev,
      empty: false,
      group,
      exclude: [],
      showRefreshButton: false
    }));
  };

  const onReloadClick = () => {
    TeaserDetailService({ tid: state.teaser.id })
      .then(data => {
        setState(prev => ({
          ...prev,
          showReloadButton: false,
          teaser: data
        }));
      })
      .catch(e => {
        console.log(e);
      });
  };

  const empty = state.empty || state.loading;
  const showLoader = state.empty == null || state.loading;

  return (
    <TeaserModerationView
      showRefreshButton={state.showRefreshButton}
      onRefreshClick={onRefreshClick}
      showReloadButton={state.showReloadButton}
      onReloadClick={onReloadClick}
      moderationGroups={availableOptions}
      group={state.group}
      onGroupChange={onGroupChange}
      teaser={state.teaser}
      onSkipClick={onSkipClick}
      isFormValid={isValid(state.teaser || {})}
      onModerationClick={onModerationClick}
      empty={empty}
      onCampaignChange={onCampaignChange}
      rejectReasons={rejectReasons}
      onReasonChange={onReasonChange}
      campaignInfo={campaignInfo}
      campaignInfoLoading={campaignInfoLoading}
      loading={showLoader}
      onFormChange={onFormChange}
      errors={state.errors}
      spellChecker={spellChecker}
    />
  );
};

TeaserModeration.defaultProps = {};

export default withQs(TeaserModeration);
